/**
 * Trial Start Modal Base
 *
 * Shared base component for trial start modals (Clinician, Evidence Pro).
 * Uses clinician trial modal styling.
 *
 * Figma (Clinician): https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7454-168922&m=dev
 * Figma (Evidence): https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7374-19523
 * Figma (Practice): https://www.figma.com/design/edgIeajMOqpAxGbdqcq2oZ/Sign-up---onboarding?node-id=9645-4022&m=dev
 */

import type { Feature } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import Loader from '@/components/ui/loader';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { Button } from '@heidi/design-system/button';
import { DialogFooter, DialogHeader } from '@heidi/design-system/dialog';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyH2,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { DialogTitle } from '@radix-ui/react-dialog';
import { Gift } from 'lucide-react';
import type { ReactNode } from 'react';

const FeatureListItem = ({ icon: Icon, description }: Feature) => (
  <div className="flex items-center gap-3">
    <div className="shrink-0 text-text-primary">
      <Icon size={24} aria-hidden="true" />
    </div>
    <TypographyP className="text-text-primary">{description}</TypographyP>
  </div>
);

export interface TrialStartModalBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dismissible?: boolean;
  hideCloseIcon?: boolean;
  title: string;
  /** Subtitle supports rich text via <Trans> (e.g. <bold> in locale strings) */
  subtitle: ReactNode;
  features: Array<Feature>;
  /** Footer caption below CTA; omitted when not provided */
  footerText?: string;
  /**
   * Footer typography size. Defaults to caption (12px) so shared consumers
   * (e.g. PracticeTrialModal) are unaffected by Heidi 2 footnote styling.
   */
  footerTypography?: 'caption' | 'footnote';
  onPrimaryClick: () => void;
  isPrimaryLoading?: boolean;
  variant?: 'pro' | 'practice';
  /** Custom CTA label; defaults to common.getStarted */
  primaryButtonText?: string;
  /** Header icon: default (tier icon) or gift (for trial "on us" messaging) */
  headerIcon?: 'default' | 'gift';
  /** Optional secondary button rendered below the primary button */
  secondaryButton?: ReactNode;
  /** Optional test id for the primary CTA. */
  primaryButtonTestId?: string;
  /** Hide the tier icon on the primary CTA (Figma practice trial has text-only primary). */
  hidePrimaryButtonIcon?: boolean;
}

export function TrialStartModalBase({
  open,
  onOpenChange,
  dismissible = true,
  hideCloseIcon = false,
  title,
  subtitle,
  features,
  footerText,
  footerTypography = 'caption',
  onPrimaryClick,
  isPrimaryLoading = false,
  variant = 'pro',
  primaryButtonText,
  headerIcon = 'default',
  secondaryButton,
  primaryButtonTestId,
  hidePrimaryButtonIcon = false,
}: TrialStartModalBaseProps) {
  const { t } = useTranslations();

  const ButtonIcon = variant === 'practice' ? TierPracticeFilled : TierProFilled;
  const ctaLabel = primaryButtonText ?? t('common.getStarted');
  const tierState = variant === 'practice' ? 'practice' : 'pro';
  const showGiftIcon = headerIcon === 'gift';

  return (
    <DialogDrawer
      open={open}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
      dismissible={dismissible}
    >
      <DialogDrawerContent
        className="mx-auto mb-auto flex w-full max-w-[480px] flex-col items-center justify-center gap-6 rounded-2xl border border-border bg-surface p-6 shadow-lg sm:min-w-[418px] sm:p-8"
        overlayClassName="backdrop-blur-sm"
        hideCloseIcon={hideCloseIcon}
        dismissible={dismissible}
      >
        {/* Header */}
        <DialogHeader className="flex flex-col items-center gap-6">
          <FramedIcon state={tierState} icon={showGiftIcon ? <Gift /> : undefined} />
          <div className="flex flex-col items-center gap-2 text-center">
            <DialogTitle asChild>
              <TypographyH2 className="whitespace-pre-line text-center text-text-primary">
                {title}
              </TypographyH2>
            </DialogTitle>
            <TypographyP2 className="text-center text-text-secondary">
              {subtitle}
            </TypographyP2>
          </div>
        </DialogHeader>

        {/* Features - vertical list */}
        <div className="flex w-full flex-col gap-4">
          {features.map((feature, index) => (
            <FeatureListItem
              key={feature.id ?? index}
              icon={feature.icon}
              description={feature.description}
            />
          ))}
        </div>

        {/* CTA Button - full width on all viewports (override DialogFooter sm:flex-row) */}
        <DialogFooter className="flex w-full flex-col items-stretch pt-6 sm:flex-col">
          <div className="flex w-full flex-col gap-3">
            <Button
              className="w-full gap-2"
              variant={variant === 'practice' ? 'pro_plus' : 'pro'}
              size="lg"
              onClick={onPrimaryClick}
              disabled={isPrimaryLoading}
              data-testid={primaryButtonTestId}
            >
              {isPrimaryLoading ? (
                <Loader size={16} />
              ) : (
                <>
                  {!hidePrimaryButtonIcon && <ButtonIcon aria-hidden />}
                  {ctaLabel}
                </>
              )}
            </Button>
            {secondaryButton != null && secondaryButton}
            {footerText != null &&
              footerText !== '' &&
              (footerTypography === 'footnote' ? (
                <TypographyFootNote className="text-center text-text-tertiary">
                  {footerText}
                </TypographyFootNote>
              ) : (
                <TypographyCaption className="text-center text-text-tertiary">
                  {footerText}
                </TypographyCaption>
              ))}
          </div>
        </DialogFooter>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
